import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  MessageCircle, 
  CheckCircle, 
  Loader2, 
  Phone, 
  ArrowLeft,
  ExternalLink,
  Info,
  Smartphone,
  Shield,
  Zap
} from 'lucide-react';
import { FaFacebook, FaWhatsapp } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

// Meta App Configuration
const META_APP_ID = '4201103220206383';
const META_CONFIG_ID = '835515812671846';
const SDK_VERSION = 'v24.0';

export default function WhatsAppOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const sessionDataRef = useRef(null);
  const [chatbots, setChatbots] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [savingAgent, setSavingAgent] = useState(false);

  // Check authentication - only redirect to login if no token
  // Don't redirect to dashboard or any other page
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('[WhatsApp Embedded] No token found, redirecting to login');
      navigate('/login');
    } else {
      console.log('[WhatsApp Embedded] Token exists, staying on page');
    }
  }, [navigate]);

  // Load Facebook SDK
  useEffect(() => {
    if (window.FB) {
      setSdkLoaded(true);
      return;
    }

    window.fbAsyncInit = function() {
      window.FB.init({
        appId: META_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: SDK_VERSION
      });
      setSdkLoaded(true);
      console.log('[WhatsApp Embedded] FB SDK initialized');
    };

    (function(d, s, id) {
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s);
      js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      js.async = true;
      js.defer = true;
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
  }, []);

  // Listen for Embedded Signup messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") {
        return;
      }

      try {
        const data = JSON.parse(event.data);
        console.log('[WhatsApp Embedded] Message received:', data);

        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          setSessionData(data);
          sessionDataRef.current = data;
          
          if (data.event === 'FINISH' && data.data) {
            console.log('[WhatsApp Embedded] Setup finished:', data.data);
          } else if (data.event === 'CANCEL') {
            setLoading(false);
            toast.error('Setup WhatsApp dibatalkan');
          } else if (data.event === 'ERROR') {
            setLoading(false);
            toast.error('Terjadi error saat setup');
          }
        }
      } catch (e) {
        // Not our message
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Check existing connection and fetch chatbots
  const checkConnection = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statusRes, chatbotsRes] = await Promise.all([
        axios.get(`${API}/whatsapp-embedded/status`, { params: { user_id: user.id } }),
        axios.get(`${API}/chatbots`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setConnectionStatus(statusRes.data);
      setChatbots(chatbotsRes.data || []);
      
      if (statusRes.data.connected) {
        if (statusRes.data.chatbot_id) {
          setStep(4); // Connected AND agent set → success
        } else {
          setStep(3); // Connected but NO agent → show agent selection
        }
      }
    } catch (error) {
      console.error('Error checking status:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  // Process FB response (async logic)
  const processFbResponse = async (response) => {
    console.log('[WhatsApp Embedded] FB Login response:', response);

    if (response.authResponse) {
      const code = response.authResponse.code;
      console.log('[WhatsApp Embedded] Got auth code, moving to step 2');
      setStep(2);
      
      // Poll for session data from ref (avoids stale closure issue)
      let attempts = 0;
      const maxAttempts = 15;
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        attempts++;
        
        const current = sessionDataRef.current;
        if (current?.data?.waba_id && current?.data?.phone_number_id) {
          break;
        }
      }
      
      const finalData = sessionDataRef.current;
      const wabaId = finalData?.data?.waba_id;
      const phoneNumberId = finalData?.data?.phone_number_id;
      
      if (!wabaId) {
        setLoading(false);
        setError('Tidak dapat mendapatkan informasi WhatsApp Business Account. Silakan coba lagi.');
        setStep(1);
        return;
      }

      try {
        const exchangeResponse = await axios.post(`${API}/whatsapp-embedded/exchange-token`, {
          code: code,
          waba_id: wabaId,
          phone_number_id: phoneNumberId,
          user_id: user.id
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        if (exchangeResponse.data.success) {
          toast.success('WhatsApp berhasil terhubung!');
          setStep(3); // Go to AI Agent selection step
          checkConnection();
        }
      } catch (error) {
        console.error('Token exchange error:', error);
        setError(error.response?.data?.detail || 'Gagal menghubungkan WhatsApp');
        setStep(1);
      }
    } else {
      console.log('[WhatsApp Embedded] FB Login cancelled or failed');
      toast.error('Login Facebook dibatalkan');
    }
  };

  // FB Login callback - wrapper for FB SDK (must be non-async)
  const fbLoginCallback = (response) => {
    console.log('[WhatsApp Embedded] fbLoginCallback triggered');
    // Call async function without awaiting (FB SDK doesn't support async callbacks)
    processFbResponse(response);
  };

  // Launch Embedded Signup
  const launchEmbeddedSignup = () => {
    console.log('[WhatsApp Embedded] launchEmbeddedSignup called');
    
    if (!sdkLoaded || !window.FB) {
      console.log('[WhatsApp Embedded] FB SDK not ready');
      toast.error('Facebook SDK belum siap. Silakan refresh halaman.');
      return;
    }

    setLoading(true);
    setError('');
    setSessionData(null);
    sessionDataRef.current = null;

    try {
      console.log('[WhatsApp Embedded] Calling FB.login...');
      window.FB.login(fbLoginCallback, {
        config_id: META_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          "version": "v3",
          "setup": {
            "business": {
              "id": null,
              "name": null,
              "email": null,
              "phone": {},
              "website": null,
              "address": {
                "streetAddress1": null,
                "streetAddress2": null,
                "city": null,
                "state": null,
                "zipPostal": null,
                "country": null
              },
              "timezone": null
            },
            "phone": {
              "displayName": null,
              "category": null,
              "description": ""
            },
            "preVerifiedPhone": { "ids": null },
            "solutionID": null,
            "whatsAppBusinessAccount": { "ids": null }
          },
          "featureType": "whatsapp_business_app_onboarding",
          "sessionInfoVersion": "3",
          "features": [{ "name": "app_only_install" }]
        }
      });
    } catch (err) {
      console.error('[WhatsApp Embedded] FB.login error:', err);
      setLoading(false);
      toast.error('Gagal membuka Facebook Login. Pastikan popup tidak diblokir.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 dark:from-slate-900 dark:to-slate-800 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/channels')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali
          </Button>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-4">
              <FaWhatsapp className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-800 dark:text-zinc-50 mb-2">
              Hubungkan WhatsApp Business
            </h1>
            <p className="text-slate-600 dark:text-zinc-400">
              Gunakan nomor WhatsApp Anda sendiri untuk chatbot AI
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                  step >= s 
                    ? 'bg-green-500 text-white' 
                    : 'bg-slate-200 dark:bg-zinc-800 text-slate-500'
                } dark:text-zinc-400`}>
                  {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {s < 4 && (
                  <div className={`w-12 h-1 rounded ${
                    step > s ? 'bg-green-500' : 'bg-slate-200 dark:bg-zinc-800'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step 1: Introduction */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Mulai Setup WhatsApp</CardTitle>
              <CardDescription>
                Hubungkan nomor WhatsApp Anda dalam 3 langkah mudah
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Requirements */}
              <div className="grid gap-4">
                <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-zinc-900 rounded-lg">
                  <FaFacebook className="w-5 h-5 text-indigo-600 mt-0.5 dark:text-indigo-400" />
                  <div>
                    <p className="font-medium text-slate-800 dark:text-zinc-50">Akun Facebook</p>
                    <p className="text-sm text-slate-600 dark:text-zinc-400">
                      Untuk login dan membuat WhatsApp Business Account
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-zinc-900 rounded-lg">
                  <Smartphone className="w-5 h-5 text-green-600 mt-0.5 dark:text-green-400" />
                  <div>
                    <p className="font-medium text-slate-800 dark:text-zinc-50">Nomor Telepon</p>
                    <p className="text-sm text-slate-600 dark:text-zinc-400">
                      Bisa nomor baru atau yang sudah pakai WhatsApp
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-zinc-900 rounded-lg">
                  <Shield className="w-5 h-5 text-amber-600 mt-0.5 dark:text-amber-400" />
                  <div>
                    <p className="font-medium text-slate-800 dark:text-zinc-50">Akses SMS</p>
                    <p className="text-sm text-slate-600 dark:text-zinc-400">
                      Untuk verifikasi nomor telepon
                    </p>
                  </div>
                </div>
              </div>

              {/* Info Alert */}
              <Alert>
                <Zap className="w-4 h-4" />
                <AlertDescription>
                  <strong>Proses cepat!</strong> Setup hanya memakan waktu 3-5 menit. 
                  Anda akan dipandu langkah demi langkah oleh Meta.
                </AlertDescription>
              </Alert>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* CTA Button */}
              <Button
                onClick={launchEmbeddedSignup}
                disabled={loading || !sdkLoaded}
                className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base"
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Memproses...
                  </>
                ) : !sdkLoaded ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Memuat...
                  </>
                ) : (
                  <>
                    <FaFacebook className="w-5 h-5 mr-2" />
                    Lanjutkan dengan Facebook
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-slate-500 dark:text-zinc-400">
                Dengan melanjutkan, Anda menyetujui{' '}
                <a 
                  href="https://www.whatsapp.com/legal/business-terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-green-600 hover:underline dark:text-green-400"
                >
                  Ketentuan WhatsApp Business
                </a>
              </p>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Processing */}
        {step === 2 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Loader2 className="w-12 h-12 text-green-500 animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-800 dark:text-zinc-50 mb-2">
                Menghubungkan WhatsApp...
              </h3>
              <p className="text-slate-600 dark:text-zinc-400">
                Mohon tunggu, kami sedang memproses koneksi Anda
              </p>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Select AI Agent */}
        {step === 3 && (
          <Card>
            <CardContent className="py-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-950/40 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-zinc-50 mb-2">
                  Pilih AI Agent
                </h3>
                <p className="text-sm text-slate-600 dark:text-zinc-400">
                  Pilih AI Agent yang akan membalas pesan WhatsApp otomatis
                </p>
              </div>
              
              {chatbots.length > 0 ? (
                <div className="max-w-sm mx-auto space-y-4">
                  <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Pilih AI Agent" />
                    </SelectTrigger>
                    <SelectContent>
                      {chatbots.map((bot) => (
                        <SelectItem key={bot.id} value={bot.id}>{bot.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button
                    onClick={async () => {
                      if (!selectedAgent) {
                        toast.error('Pilih AI Agent terlebih dahulu');
                        return;
                      }
                      setSavingAgent(true);
                      try {
                        const token = localStorage.getItem('token');
                        await axios.post(`${API}/whatsapp-embedded/set-agent`, {
                          user_id: user.id,
                          chatbot_id: selectedAgent
                        }, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success('AI Agent berhasil dipilih!');
                        setStep(4);
                      } catch (err) {
                        const detail = err.response?.data?.detail || '';
                        if (detail.includes('not found')) {
                          toast.error('Koneksi WhatsApp tidak ditemukan. Silakan hubungkan ulang.');
                          setStep(1);
                        } else {
                          toast.error('Gagal menyimpan AI Agent');
                        }
                      } finally {
                        setSavingAgent(false);
                      }
                    }}
                    disabled={!selectedAgent || savingAgent}
                    className="w-full bg-green-600 hover:bg-green-700 gap-2"
                  >
                    {savingAgent ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    Lanjutkan
                  </Button>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-amber-600 mb-3 dark:text-amber-400">Anda belum punya AI Agent.</p>
                  <Button variant="outline" onClick={() => navigate('/dashboard')}>
                    Buat AI Agent Dulu
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <Card className="border-green-200 bg-green-50 dark:bg-green-900/20">
            <CardContent className="py-8 text-center">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-green-800 dark:text-green-400 mb-2">
                WhatsApp Berhasil Terhubung!
              </h3>
              
              {connectionStatus?.connection && (
                <div className="bg-white dark:bg-zinc-900 rounded-lg p-4 mb-6 max-w-sm mx-auto">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <FaWhatsapp className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-slate-800 dark:text-zinc-50">
                        {connectionStatus.connection.phone_display_number || 'WhatsApp Business'}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-zinc-400">
                        {connectionStatus.connection.verified_name || 'Terverifikasi'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-green-700 dark:text-green-500 mb-6">
                Nomor WhatsApp Anda siap menerima pesan dari customer!
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  onClick={() => navigate('/inbox')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Buka Inbox
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                >
                  Ke Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Section */}
        <div className="mt-6 text-center">
          <p className="text-sm text-slate-500 dark:text-zinc-400">
            Butuh bantuan?{' '}
            <a 
              href="/whatsapp-guide" 
              className="text-green-600 hover:underline dark:text-green-400"
            >
              Lihat panduan lengkap
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
